// Blog articles (FR + NL) — optimisés SEO pour la Belgique (Wallonie + Flandre).
export const articles = [
  {
    slug: "fiscalite-voiture-societe-belgique-2026",
    date: "2026-01-15", readTime: "6 min",
    cover: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tr: {
      fr: {
        category: "Fiscalité",
        title: "Fiscalité des voitures de société en Belgique en 2026 : ce qui change",
        excerpt: "Déductibilité, ATN, verdissement du parc : le point complet sur la fiscalité automobile des sociétés en Belgique.",
        keywords: "fiscalité voiture société 2026 Belgique, déductibilité voiture société, ATN voiture électrique",
        content: `## Une fiscalité de plus en plus verte

La Belgique oriente sa fiscalité automobile vers l'électrification : **plus une voiture émet de CO₂, moins elle est déductible.** Les véhicules 100 % électriques restent, eux, très avantageux.

## La déductibilité des frais

Pour les voitures thermiques (essence, diesel), la déductibilité se réduit progressivement selon le calendrier fédéral, jusqu'à une extinction quasi complète pour les motorisations fossiles. Les **électriques** conservent une déductibilité élevée.

## L'avantage de toute nature (ATN)

L'ATN dépend de la valeur catalogue, de l'âge et des émissions de CO₂. Un véhicule électrique génère un ATN plus favorable qu'un équivalent thermique.

## Impact sur votre leasing ou renting

- En **leasing** (immatriculation au nom du client, au bilan) : amortissements soumis aux plafonds de déductibilité.
- En **renting** (immatriculation au nom du client, hors bilan) : loyers en charges, mêmes plafonds.

> Pour une simulation adaptée à 2026 : **067/87.05.53** · **i.lease.be@gmail.com** — réponse en 48h.

*Attention, emprunter de l'argent coûte aussi de l'argent.*`,
      },
      nl: {
        category: "Fiscaliteit",
        title: "Fiscaliteit van bedrijfswagens in België in 2026: wat verandert er?",
        excerpt: "Aftrekbaarheid, VAA, vergroening van het wagenpark: een compleet overzicht van de autofiscaliteit voor bedrijven in België.",
        keywords: "fiscaliteit bedrijfswagen 2026 België, aftrekbaarheid bedrijfswagen, VAA elektrische wagen",
        content: `## Een steeds groenere fiscaliteit

België stuurt zijn autofiscaliteit richting elektrificatie: **hoe meer CO₂ een wagen uitstoot, hoe minder hij aftrekbaar is.** Volledig elektrische wagens blijven zeer voordelig.

## Aftrekbaarheid van de kosten

Voor thermische wagens (benzine, diesel) daalt de aftrekbaarheid geleidelijk volgens de federale kalender, tot bijna volledige uitdoving voor fossiele motoren. **Elektrische** wagens behouden een hoge aftrekbaarheid.

## Het voordeel van alle aard (VAA)

Het VAA hangt af van de cataloguswaarde, de leeftijd en de CO₂-uitstoot. Een elektrische wagen geeft een gunstiger VAA dan een thermisch equivalent.

## Impact op uw leasing of renting

- Bij **leasing** (inschrijving op naam van de klant, op balans): afschrijvingen onderworpen aan de aftrekplafonds.
- Bij **renting** (inschrijving op naam van de klant, buiten balans): huur als kosten, zelfde plafonds.

> Voor een simulatie op maat van 2026: **067/87.05.53** · **i.lease.be@gmail.com** — antwoord binnen 48u.

*Let op, geld lenen kost ook geld.*`,
      },
    },
  },
  {
    slug: "leasing-renting-voiture-electrique-2026",
    date: "2026-02-10", readTime: "5 min",
    cover: "https://images.pexels.com/photos/4678065/pexels-photo-4678065.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1200",
    tr: {
      fr: {
        category: "Électrique",
        title: "Leasing ou renting pour une voiture électrique en 2026 ?",
        excerpt: "Batterie, bornes, valeur résiduelle, TCO : comment financer intelligemment un véhicule électrique de société en Belgique.",
        keywords: "leasing voiture électrique Belgique, renting véhicule électrique, TCO voiture électrique société",
        content: `## Pourquoi financer plutôt qu'acheter ?

Financer un véhicule électrique en **leasing** ou **renting** préserve la trésorerie, offre une fiscalité encore favorable et n'immobilise pas de capital.

## Leasing ou renting : que choisir ?

- **Leasing** : immatriculation au nom du client, au bilan, valeur résiduelle 1 % à 15 %. Idéal pour **devenir propriétaire**.
- **Renting** : immatriculation au nom du client, hors bilan, valeur résiduelle 16 % à 25 %. Idéal pour **préserver vos lignes de crédit** et changer souvent.

## La valeur résiduelle

Pour l'électrique, elle dépend de la santé de la batterie et de l'évolution technologique. Un bon financement l'anticipe pour équilibrer vos loyers.

## Le TCO, seul vrai indicateur

Regardez le **coût total de possession** (énergie, entretien, fiscalité, valeur résiduelle) : l'électrique gagne souvent sur la durée.

> I-LEASE compare les formules et les marques partout en Belgique. **067/87.05.53**.

*Attention, emprunter de l'argent coûte aussi de l'argent.*`,
      },
      nl: {
        category: "Elektrisch",
        title: "Leasing of renting voor een elektrische wagen in 2026?",
        excerpt: "Batterij, laadpalen, restwaarde, TCO: hoe u slim een elektrische bedrijfswagen financiert in België.",
        keywords: "leasing elektrische wagen België, renting elektrisch voertuig, TCO elektrische bedrijfswagen",
        content: `## Waarom financieren in plaats van kopen?

Een elektrische wagen financieren via **leasing** of **renting** spaart uw cashflow, biedt een nog gunstige fiscaliteit en legt geen kapitaal vast.

## Leasing of renting: wat kiezen?

- **Leasing**: inschrijving op naam van de klant, op balans, restwaarde 1 % tot 15 %. Ideaal om **eigenaar te worden**.
- **Renting**: inschrijving op naam van de klant, buiten balans, restwaarde 16 % tot 25 %. Ideaal om **uw kredietlijnen vrij te houden** en vaak te wisselen.

## De restwaarde

Voor elektrisch hangt ze af van de batterijgezondheid en de technologische evolutie. Een goede financiering anticipeert hierop om uw huur in evenwicht te brengen.

## De TCO, de enige echte indicator

Kijk naar de **totale eigendomskost** (energie, onderhoud, fiscaliteit, restwaarde): elektrisch wint vaak op termijn.

> I-LEASE vergelijkt formules en merken overal in België. **067/87.05.53**.

*Let op, geld lenen kost ook geld.*`,
      },
    },
  },
  {
    slug: "financer-camion-engin-genie-civil-belgique",
    date: "2026-03-05", readTime: "5 min",
    cover: "https://images.pexels.com/photos/31293900/pexels-photo-31293900.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1200",
    tr: {
      fr: {
        category: "Professionnels",
        title: "Financer un camion ou un engin de génie civil en Belgique",
        excerpt: "Poids lourds, transport routier, machines de chantier : les solutions de leasing et renting adaptées aux pros.",
        keywords: "leasing camion Belgique, financement engin génie civil, leasing poids lourd, renting machine chantier",
        content: `## Au-delà de l'automobile

Chez **I-LEASE**, nous finançons aussi les **camions**, le **transport routier** et les **engins de génie civil** (pelleteuses, chargeuses, matériel de chantier), partout en Belgique.

## Pourquoi financer plutôt qu'acheter ?

- Préserver votre trésorerie et vos lignes de crédit
- Étaler l'investissement sur la durée d'utilisation
- Bénéficier d'un cadre fiscal adapté

## Leasing, renting ou LLD ?

- **Leasing** : inscription au nom du client, valeur résiduelle faible, rachat du bien en fin de contrat.
- **Renting** : hors bilan, immatriculation au nom du client, valeur résiduelle 16 % à 25 %.
- **LLD** : location longue durée, idéale sans volonté de rachat.

## Une réponse en 48h

Envoyez votre bon de commande via notre formulaire sécurisé : dès que le dossier est complet, **réponse en 48 heures ouvrables**.

> Un projet de camion ou d'engin ? **067/87.05.53** · **i.lease.be@gmail.com**.

*Attention, emprunter de l'argent coûte aussi de l'argent.*`,
      },
      nl: {
        category: "Professionals",
        title: "Een vrachtwagen of graafmachine financieren in België",
        excerpt: "Vrachtwagens, wegtransport, bouwmachines: de leasing- en rentingoplossingen op maat van professionals.",
        keywords: "leasing vrachtwagen België, financiering bouwmaterieel, leasing zwaar voertuig, renting bouwmachine",
        content: `## Meer dan alleen auto's

Bij **I-LEASE** financieren wij ook **vrachtwagens**, **wegtransport** en **machines voor civiele techniek** (graafmachines, laders, bouwmaterieel), overal in België.

## Waarom financieren in plaats van kopen?

- Uw cashflow en kredietlijnen sparen
- De investering spreiden over de gebruiksduur
- Een aangepast fiscaal kader benutten

## Leasing, renting of LLD?

- **Leasing**: inschrijving op naam van de klant, lage restwaarde, aankoop op het einde.
- **Renting**: buiten balans, inschrijving op naam van de klant, restwaarde 16 % tot 25 %.
- **LLD**: langlopende verhuur, ideaal zonder aankoopintentie.

## Antwoord binnen 48u

Stuur uw bestelbon via ons beveiligd formulier: zodra het dossier compleet is, **antwoord binnen 48 werkuren**.

> Een project voor een vrachtwagen of machine? **067/87.05.53** · **i.lease.be@gmail.com**.

*Let op, geld lenen kost ook geld.*`,
      },
    },
  },
  {
    slug: "tva-voiture-societe-belgique",
    date: "2026-03-20", readTime: "5 min",
    cover: "https://images.unsplash.com/photo-1621378864046-0122e4a415a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tr: {
      fr: {
        category: "TVA",
        title: "TVA et voiture de société en Belgique : ce que vous pouvez récupérer",
        excerpt: "Règle des 50 %, méthodes de calcul, leasing vs renting : comment optimiser la TVA sur votre véhicule professionnel.",
        keywords: "TVA voiture société Belgique, récupération TVA leasing, déduction TVA véhicule professionnel",
        content: `## La règle de base : maximum 50 %

En Belgique, la déduction de la TVA sur une voiture mixte (usage pro et privé) est **plafonnée à 50 %**, même si l'usage professionnel est supérieur. Ce plafond s'applique à l'achat, au leasing, au carburant et à l'entretien.

## Les méthodes de calcul de l'usage professionnel

L'administration prévoit plusieurs méthodes (registre des trajets, semi-forfait, forfait de 35 %). Le pourcentage réel de déduction est ensuite limité à 50 % maximum.

## Leasing et renting : quel impact TVA ?

- En **leasing** et en **renting**, la TVA suit les **loyers** (pas de préfinancement de la TVA sur la valeur totale).
- La récupération dépend de votre pourcentage d'usage professionnel, plafonné à 50 %.

## Camions et utilitaires

Les véhicules utilitaires (camionnettes, camions) ne sont pas soumis à la limite des 50 % : la TVA peut souvent être déduite selon l'usage professionnel réel.

> La TVA dépend de votre situation précise. I-LEASE structure votre financement pour l'optimiser : **067/87.05.53**.

*Attention, emprunter de l'argent coûte aussi de l'argent.*`,
      },
      nl: {
        category: "BTW",
        title: "Btw en bedrijfswagen in België: wat u kunt recupereren",
        excerpt: "De 50 %-regel, berekeningsmethodes, leasing vs renting: hoe u de btw op uw professioneel voertuig optimaliseert.",
        keywords: "btw bedrijfswagen België, btw-recuperatie leasing, btw-aftrek professioneel voertuig",
        content: `## De basisregel: maximaal 50 %

In België is de btw-aftrek op een gemengde wagen (professioneel en privé) **beperkt tot 50 %**, zelfs bij hoger professioneel gebruik. Dit plafond geldt voor aankoop, leasing, brandstof en onderhoud.

## De berekeningsmethodes van het beroepsgebruik

De administratie voorziet meerdere methodes (rittenregister, semi-forfait, forfait van 35 %). Het reële aftrekpercentage is daarna beperkt tot maximaal 50 %.

## Leasing en renting: welke btw-impact?

- Bij **leasing** en **renting** volgt de btw de **huur** (geen voorfinanciering van de btw op de totale waarde).
- De recuperatie hangt af van uw professioneel gebruikspercentage, beperkt tot 50 %.

## Vrachtwagens en bestelwagens

Bedrijfsvoertuigen (bestelwagens, vrachtwagens) vallen niet onder de 50 %-limiet: de btw kan vaak volgens het reële professioneel gebruik worden afgetrokken.

> De btw hangt af van uw situatie. I-LEASE structureert uw financiering om ze te optimaliseren: **067/87.05.53**.

*Let op, geld lenen kost ook geld.*`,
      },
    },
  },
  {
    slug: "comprendre-valeur-residuelle-leasing",
    date: "2026-04-08", readTime: "4 min",
    cover: "https://images.unsplash.com/photo-1653565217811-85b41bcd1edb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tr: {
      fr: {
        category: "Valeur résiduelle",
        title: "Comprendre la valeur résiduelle en leasing et renting",
        excerpt: "1 % ou 25 % ? La valeur résiduelle détermine vos loyers et le rachat du véhicule. On vous explique tout.",
        keywords: "valeur résiduelle leasing, valeur résiduelle renting, rachat véhicule fin de contrat, option d'achat leasing",
        content: `## Qu'est-ce que la valeur résiduelle ?

C'est le **montant de rachat** de votre véhicule à la fin du contrat, fixé au départ. Plus elle est basse, plus vos loyers sont élevés (et inversement).

## Les fourchettes en Belgique

- **Leasing** : de **1 % à 15 %** (faible). À la fin, le client rachète le véhicule à ce montant.
- **Renting** : de **16 % à 25 %**. Vous pouvez restituer ou racheter selon le contrat.
- **LLD** : généralement **sans option d'achat** — vous restituez le véhicule.

## Comment bien la choisir ?

Une valeur résiduelle basse allège le rachat final mais alourdit les loyers ; une valeur élevée fait l'inverse. Le bon réglage dépend de votre trésorerie et de votre intention (garder ou rendre le bien).

## Notre rôle

I-LEASE calibre la valeur résiduelle en fonction du véhicule, de la durée et de votre usage, pour des mensualités cohérentes.

> Besoin d'une simulation ? **067/87.05.53** · réponse en 48h.

*Attention, emprunter de l'argent coûte aussi de l'argent.*`,
      },
      nl: {
        category: "Restwaarde",
        title: "De restwaarde in leasing en renting begrijpen",
        excerpt: "1 % of 25 %? De restwaarde bepaalt uw huur en de aankoop van het voertuig. We leggen alles uit.",
        keywords: "restwaarde leasing, restwaarde renting, aankoop voertuig einde contract, aankoopoptie leasing",
        content: `## Wat is de restwaarde?

Het is het **aankoopbedrag** van uw voertuig op het einde van het contract, vooraf vastgelegd. Hoe lager, hoe hoger uw huur (en omgekeerd).

## De marges in België

- **Leasing**: van **1 % tot 15 %** (laag). Op het einde koopt de klant het voertuig aan dit bedrag.
- **Renting**: van **16 % tot 25 %**. U kunt teruggeven of aankopen volgens het contract.
- **LLD**: doorgaans **zonder aankoopoptie** — u geeft het voertuig terug.

## Hoe goed kiezen?

Een lage restwaarde verlicht de eindaankoop maar verhoogt de huur; een hoge doet het omgekeerde. De juiste instelling hangt af van uw cashflow en intentie.

## Onze rol

I-LEASE kalibreert de restwaarde volgens voertuig, looptijd en gebruik, voor coherente maandbedragen.

> Een simulatie nodig? **067/87.05.53** · antwoord binnen 48u.

*Let op, geld lenen kost ook geld.*`,
      },
    },
  },
  {
    slug: "leasing-independant-belgique",
    date: "2026-04-22", readTime: "4 min",
    cover: "https://images.unsplash.com/photo-1586880244406-556ebe35f282?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tr: {
      fr: {
        category: "Indépendants",
        title: "Leasing pour indépendants et petites sociétés en Belgique",
        excerpt: "Vous êtes indépendant ou gérant d'une petite structure ? Voici comment financer votre véhicule sans immobiliser votre trésorerie.",
        keywords: "leasing indépendant Belgique, leasing petite société, financement voiture indépendant, renting indépendant",
        content: `## Une solution pour chaque indépendant

Indépendant, profession libérale ou petite société : vous pouvez financer votre véhicule en **leasing** ou **renting**, sans mobiliser votre épargne ni vos lignes bancaires.

## Les avantages concrets

- Mensualités fixes, budget maîtrisé
- Fiscalité adaptée (amortissement ou charges)
- Possibilité d'inclure entretien, assurance et pneus

## Quels documents fournir ?

Votre bon de commande, votre carte d'identité recto/verso et votre dernier avertissement extrait de rôle. Des fiches de revenus peuvent être demandées. Tout se transmet via notre formulaire sécurisé (RGPD).

## Un accompagnement humain

En tant que **courtier indépendant**, I-LEASE compare pour vous les organismes de financement et négocie les meilleures conditions — même pour un premier dossier.

> Lancez votre projet : **067/87.05.53** · **i.lease.be@gmail.com** — réponse en 48h.

*Attention, emprunter de l'argent coûte aussi de l'argent.*`,
      },
      nl: {
        category: "Zelfstandigen",
        title: "Leasing voor zelfstandigen en kleine vennootschappen in België",
        excerpt: "Zelfstandige of zaakvoerder van een kleine structuur? Zo financiert u uw voertuig zonder uw cashflow vast te leggen.",
        keywords: "leasing zelfstandige België, leasing kleine vennootschap, financiering wagen zelfstandige, renting zelfstandige",
        content: `## Een oplossing voor elke zelfstandige

Zelfstandige, vrij beroep of kleine vennootschap: u kunt uw voertuig financieren via **leasing** of **renting**, zonder uw spaargeld of bankkredieten aan te spreken.

## De concrete voordelen

- Vaste maandbedragen, beheerst budget
- Aangepaste fiscaliteit (afschrijving of kosten)
- Mogelijkheid om onderhoud, verzekering en banden op te nemen

## Welke documenten bezorgen?

Uw bestelbon, uw identiteitskaart voor/achter en uw laatste aanslagbiljet. Inkomstenfiches kunnen gevraagd worden. Alles via ons beveiligd formulier (GDPR).

## Menselijke begeleiding

Als **onafhankelijke makelaar** vergelijkt I-LEASE de financieringsinstellingen en onderhandelt de beste voorwaarden — ook voor een eerste dossier.

> Start uw project: **067/87.05.53** · **i.lease.be@gmail.com** — antwoord binnen 48u.

*Let op, geld lenen kost ook geld.*`,
      },
    },
  },
  {
    slug: "leasing-camionnette-electrique-belgique",
    date: "2026-05-06", readTime: "5 min",
    cover: "https://images.unsplash.com/photo-1732193933813-4da17265a36c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tr: {
      fr: {
        category: "Utilitaires",
        title: "Leasing d'une camionnette électrique en Belgique en 2026",
        excerpt: "Autonomie, recharge, TVA 100 %, primes : pourquoi la camionnette électrique en leasing séduit les pros belges.",
        keywords: "leasing camionnette électrique Belgique, renting utilitaire électrique, leasing bestelwagen elektrisch",
        content: `## L'utilitaire électrique, un vrai levier fiscal

Contrairement aux voitures, les **camionnettes et utilitaires** ne subissent pas la limite de déduction TVA de 50 % : la **TVA peut souvent être récupérée à 100 %** selon l'usage professionnel réel. En version électrique, l'avantage se cumule avec une fiscalité verte favorable.

## Autonomie et recharge : ce qui a changé

Les utilitaires électriques récents dépassent largement les besoins d'une tournée urbaine ou régionale. Une **borne à dépôt** et la recharge de nuit couvrent la majorité des usages professionnels.

## Leasing ou renting pour un utilitaire ?

- **Leasing** : immatriculation au nom du client, au bilan, valeur résiduelle basse — idéal pour devenir propriétaire de sa flotte.
- **Renting** : immatriculation au nom du client, hors bilan, valeur résiduelle 16 % à 25 % — idéal pour renouveler souvent sans immobiliser de capital.

## Notre accompagnement

I-LEASE compare les marques, les autonomies et les formules (avec ou sans entretien, pneus, assistance) pour bâtir le meilleur coût total.

> Un projet d'utilitaire électrique ? **067/87.05.53** · **i.lease.be@gmail.com** — réponse en 48h.

*Attention, emprunter de l'argent coûte aussi de l'argent.*`,
      },
      nl: {
        category: "Bestelwagens",
        title: "Een elektrische bestelwagen leasen in België in 2026",
        excerpt: "Rijbereik, laden, 100 % btw, premies: waarom de elektrische bestelwagen in leasing Belgische pro's overtuigt.",
        keywords: "leasing elektrische bestelwagen België, renting elektrische bestelwagen, elektrische bestelwagen leasen",
        content: `## De elektrische bestelwagen, een echte fiscale hefboom

In tegenstelling tot personenwagens vallen **bestelwagens en lichte vracht** niet onder de btw-aftrekbeperking van 50 %: de **btw kan vaak voor 100 % worden gerecupereerd** volgens het reële professioneel gebruik. In elektrische versie komt daar een gunstige groene fiscaliteit bovenop.

## Rijbereik en laden: wat is er veranderd

Recente elektrische bestelwagens overtreffen ruimschoots de noden van een stads- of regionale ronde. Een **laadpaal op de vestiging** en 's nachts laden dekken de meeste professionele toepassingen.

## Leasing of renting voor een bestelwagen?

- **Leasing**: inschrijving op naam van de klant, op balans, lage restwaarde — ideaal om eigenaar te worden van uw wagenpark.
- **Renting**: inschrijving op naam van de klant, buiten balans, restwaarde 16 % tot 25 % — ideaal om vaak te vernieuwen zonder kapitaal vast te leggen.

## Onze begeleiding

I-LEASE vergelijkt merken, rijbereiken en formules (met of zonder onderhoud, banden, bijstand) om de beste totale kost te bouwen.

> Een project voor een elektrische bestelwagen? **067/87.05.53** · **i.lease.be@gmail.com** — antwoord binnen 48u.

*Let op, geld lenen kost ook geld.*`,
      },
    },
  },
  {
    slug: "leasing-voiture-occasion-belgique",
    date: "2026-05-21", readTime: "4 min",
    cover: "https://images.unsplash.com/photo-1692406069831-0bb7ea297645?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tr: {
      fr: {
        category: "Occasion",
        title: "Leasing d'une voiture d'occasion en Belgique : est-ce possible ?",
        excerpt: "Oui, le leasing d'occasion existe. Conditions d'âge, kilométrage, avantages budget : le guide complet.",
        keywords: "leasing voiture occasion Belgique, renting seconde main, leasing tweedehands wagen Belgique",
        content: `## Le leasing d'occasion, une vraie option

On peut parfaitement financer un véhicule **de seconde main** en leasing ou en renting. C'est une solution maligne pour rouler dans une gamme supérieure tout en maîtrisant le budget.

## Les conditions habituelles

- **Âge du véhicule** : généralement jusqu'à quelques années, avec une limite en fin de contrat.
- **Kilométrage** : plafonné selon l'état et l'historique d'entretien.
- **Contrôle technique et historique** : indispensables pour valider le dossier.

## Pourquoi choisir l'occasion ?

- Des **loyers plus bas** qu'un véhicule neuf équivalent.
- Une **décote déjà absorbée** par le premier propriétaire.
- L'accès à des modèles premium à budget maîtrisé.

## Leasing ou renting ?

- **Leasing** : immatriculation au nom du client, rachat possible en fin de contrat.
- **Renting** : hors bilan, immatriculation au nom du client, valeur résiduelle 16 % à 25 %.

> Envie d'une occasion en leasing ? Envoyez le bon de commande : **réponse en 48h**. **067/87.05.53**.

*Attention, emprunter de l'argent coûte aussi de l'argent.*`,
      },
      nl: {
        category: "Tweedehands",
        title: "Een tweedehands wagen leasen in België: kan dat?",
        excerpt: "Ja, tweedehands leasing bestaat. Leeftijd, kilometerstand, budgetvoordelen: de complete gids.",
        keywords: "leasing tweedehands wagen België, tweedehands leasen, occasion leasing België, renting tweedehands",
        content: `## Tweedehands leasing, een echte optie

U kunt perfect een **tweedehands voertuig** financieren via leasing of renting. Het is een slimme oplossing om in een hoger segment te rijden met een beheerst budget.

## De gebruikelijke voorwaarden

- **Leeftijd van het voertuig**: doorgaans tot enkele jaren, met een limiet op het einde van het contract.
- **Kilometerstand**: begrensd volgens staat en onderhoudshistoriek.
- **Keuring en historiek**: onmisbaar om het dossier te valideren.

## Waarom tweedehands kiezen?

- **Lagere huur** dan een gelijkwaardige nieuwe wagen.
- Een **reeds opgevangen waardevermindering** door de eerste eigenaar.
- Toegang tot premiummodellen met een beheerst budget.

## Leasing of renting?

- **Leasing**: inschrijving op naam van de klant, aankoop mogelijk op het einde.
- **Renting**: buiten balans, inschrijving op naam van de klant, restwaarde 16 % tot 25 %.

> Zin in een tweedehands in leasing? Stuur de bestelbon: **antwoord binnen 48u**. **067/87.05.53**.

*Let op, geld lenen kost ook geld.*`,
      },
    },
  },
  {
    slug: "location-operationnelle-flotte-belgique",
    date: "2026-06-04", readTime: "5 min",
    cover: "https://images.unsplash.com/photo-1616363088386-31c4a8414858?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tr: {
      fr: {
        category: "Flotte",
        title: "Location opérationnelle (LLD) pour gérer votre flotte en Belgique",
        excerpt: "Loyer tout compris, hors bilan, zéro souci de revente : la location opérationnelle pour les flottes d'entreprise.",
        keywords: "location opérationnelle Belgique, LLD flotte entreprise, operationele lease België, gestion de flotte",
        content: `## La location opérationnelle, c'est quoi ?

La **location opérationnelle** (aussi appelée LLD ou *operationele lease*) est une location longue durée où l'entreprise paie un **loyer tout compris** et **restitue** le véhicule en fin de contrat. Pas de valeur résiduelle à gérer, pas de risque de revente.

## Les avantages pour une flotte

- **Hors bilan** : les véhicules n'apparaissent pas à l'actif, vos ratios financiers restent sains.
- **Budget prévisible** : loyer fixe incluant entretien, pneus, assurance et assistance selon la formule.
- **Zéro gestion de revente** : vous rendez simplement le véhicule.
- **Renouvellement fluide** : une flotte toujours récente et fiable.

## LLD vs leasing vs renting

- **LLD / location opérationnelle** : immatriculation au nom du bailleur, restitution, généralement sans option d'achat.
- **Leasing** : immatriculation au nom du client, rachat en fin de contrat.
- **Renting** : immatriculation au nom du client, hors bilan, valeur résiduelle 16 % à 25 %.

## Notre rôle sur les flottes

I-LEASE structure votre parc (voitures, utilitaires, camions) avec un interlocuteur unique et une réponse en 48h.

> Vous gérez une flotte ? Parlons-en : **067/87.05.53** · **i.lease.be@gmail.com**.

*Attention, emprunter de l'argent coûte aussi de l'argent.*`,
      },
      nl: {
        category: "Wagenpark",
        title: "Operationele lease om uw wagenpark te beheren in België",
        excerpt: "All-in huur, buiten balans, geen zorgen over doorverkoop: operationele lease voor bedrijfsvloten.",
        keywords: "operationele lease België, operationele leasing wagenpark, LLD vloot bedrijf, wagenparkbeheer België",
        content: `## Wat is operationele lease?

**Operationele lease** (ook LLD of langlopende verhuur genoemd) is een langlopende verhuur waarbij de onderneming een **all-in huur** betaalt en het voertuig **teruggeeft** op het einde van het contract. Geen restwaarde om te beheren, geen risico bij doorverkoop.

## De voordelen voor een wagenpark

- **Buiten balans**: de voertuigen staan niet op de actiefzijde, uw financiële ratio's blijven gezond.
- **Voorspelbaar budget**: vaste huur inclusief onderhoud, banden, verzekering en bijstand volgens formule.
- **Geen doorverkoopbeheer**: u geeft het voertuig gewoon terug.
- **Vlotte vernieuwing**: een steeds recent en betrouwbaar wagenpark.

## LLD vs leasing vs renting

- **Operationele lease / LLD**: inschrijving op naam van de verhuurder, teruggave, doorgaans zonder aankoopoptie.
- **Leasing**: inschrijving op naam van de klant, aankoop op het einde.
- **Renting**: inschrijving op naam van de klant, buiten balans, restwaarde 16 % tot 25 %.

## Onze rol bij wagenparken

I-LEASE structureert uw vloot (wagens, bestelwagens, vrachtwagens) met één aanspreekpunt en een antwoord binnen 48u.

> Beheert u een wagenpark? Laten we praten: **067/87.05.53** · **i.lease.be@gmail.com**.

*Let op, geld lenen kost ook geld.*`,
      },
    },
  },
];

export const getArticle = (slug) => articles.find((a) => a.slug === slug);
export const trArticle = (a, lang) => (a.tr[lang] || a.tr.fr);
